"use client";

import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Box, Spinner, Flex, useToast } from "@chakra-ui/react";
import { AgentConfig } from "./AgentConfig.tsx";
import { MultiPromptAgentConfig } from "./MultiPromptAgentConfig.tsx";
import { CustomLLMConfig } from "./CustomLLMConfig.tsx";
import {
  agentService,
  type Agent,
  type RetellLLM,
} from "../../services/agentService.ts";
import { useAppTheme } from "../../ThemeContext.tsx";

const AgentRouter = () => {
  const { agentId } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { resolvedTheme } = useAppTheme();
  const isDark = resolvedTheme === "dark";

  const [agent, setAgent] = useState<Agent | null>(null);
  const [llm, setLlm] = useState<RetellLLM | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [agentType, setAgentType] = useState<
    "single-prompt" | "multi-prompt" | "custom-llm" | null
  >(null);

  useEffect(() => {
    const fetchAgentData = async () => {
      if (!agentId) {
        navigate("/agents");
        return;
      }

      try {
        setIsLoading(true);
        const agentData = await agentService.get(agentId);
        setAgent(agentData);

        // Determine agent type
        if (agentData.response_engine.type === "custom-llm") {
          setAgentType("custom-llm");
        } else if (agentData.response_engine.type === "retell-llm") {
          // Fetch LLM data to determine if it's multi-prompt
          const llmData = await agentService.getRetellLLM(
            agentData.response_engine.llm_id
          );
          setLlm(llmData);

          if (llmData.states && Array.isArray(llmData.states)) {
            setAgentType("multi-prompt");
          } else {
            setAgentType("single-prompt");
          }
        }
      } catch (error) {
        console.error("Error fetching agent:", error);
        toast({
          title: "Error loading agent",
          description: "Could not load agent data",
          status: "error",
          duration: 3000,
        });
        navigate("/agents");
      } finally {
        setIsLoading(false);
      }
    };

    fetchAgentData();
  }, [agentId, navigate, toast]);

  if (isLoading) {
    return (
      <Flex
        justify="center"
        align="center"
        h="100vh"
        bg={isDark ? "gray.900" : "gray.100"}
      >
        <Spinner size="xl" />
      </Flex>
    );
  }

  if (!agent) {
    return <Box>Agent not found</Box>;
  }

  // Render the appropriate component based on agent type
  if (agentType === "custom-llm") {
    return <CustomLLMConfig agent={agent} />;
  } else if (agentType === "multi-prompt") {
    return <MultiPromptAgentConfig agent={agent} />;
  } else {
    return <AgentConfig agent={agent} />;
  }
};

export default AgentRouter;
